import React from "react";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl, onMediaImgError } from "@/components/FileUpload";
import VideoPlayer from "@/components/VideoPlayer";
import { avatarSrc } from "@/lib/defaults";
import { ctaLabel } from "@/lib/adCta";
import { ChevronRight } from "lucide-react";

// Live "façon Facebook" preview of the ad while the advertiser fills the form.
export const AdPreview = ({ form, user }) => {
  const { t } = useApp();
  const hasButton = form.cta_type && form.cta_type !== "none";
  const typeLabel = form.cta_type === "lead_form" ? t("ads.preview_type_form", "Formulaire")
    : form.cta_type === "url" ? t("ads.preview_type_web", "Site web")
    : t("ads.preview_type_sponsored", "Sponsorisé");
  return (
    <div className="yalola-card overflow-hidden" data-testid="ad-preview">
      <div className="px-4 pt-3 flex items-center gap-2">
        <img src={avatarSrc(user)} alt="" className="h-9 w-9 rounded-full object-cover" />
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{user?.name || t("ads.preview_page", "Votre page")}</div>
          <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("ads.preview_sponsored", "Sponsorisé · à l'instant")}</div>
        </div>
      </div>
      {form.body && <p className="px-4 pt-2 text-sm whitespace-pre-wrap">{form.body}</p>}
      <div className="mt-2">
        {form.media_url ? (
          form.media_type === "video"
            ? <VideoPlayer mode="controls" src={form.media_url} poster={form.poster_url} className="w-full aspect-video" testId="ad-preview-video" />
            : <img src={resolveMediaUrl(form.media_url)} onError={onMediaImgError} alt="" className="w-full max-h-[70vh] object-contain bg-black/5" />
        ) : (
          <div className="w-full aspect-video grid place-items-center" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>
            <span className="text-xs">{t("ads.preview_visual_ph", "Votre visuel apparaîtra ici")}</span>
          </div>
        )}
      </div>
      {(hasButton || form.cta_headline || form.cta_description) && (
        <div className="m-3 rounded-2xl border flex items-center gap-3 p-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{typeLabel}</div>
            {form.cta_headline && <div className="text-sm font-bold leading-snug truncate">{form.cta_headline}</div>}
            {form.cta_description && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{form.cta_description}</div>}
          </div>
          {hasButton && <span className="pill pill-primary shrink-0 whitespace-nowrap">{t(`ads.ctalabel_${form.cta_label}`, ctaLabel({ label: form.cta_label }))} <ChevronRight size={15} className="ml-0.5" /></span>}
        </div>
      )}
      <div className="px-4 py-2 border-t flex items-center justify-around text-xs font-semibold" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)" }}>
        <span>👍 {t("ads.preview_like", "J'aime")}</span><span>💬 {t("ads.preview_comment", "Commenter")}</span><span>↗ {t("ads.preview_share", "Partager")}</span>
      </div>
    </div>
  );
};

// Live "Reels" (9:16 vertical, full-bleed) preview — shows how the ad looks in
// the Reels placement so advertisers can check both feed & reels before publishing.
export const ReelsAdPreview = ({ form, user }) => {
  const { t } = useApp();
  const hasButton = form.cta_type && form.cta_type !== "none";
  return (
    <div className="mx-auto w-full max-w-[260px]" data-testid="ad-preview-reels">
      <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-lg">
        {form.media_url ? (
          form.media_type === "video"
            ? <div className="absolute inset-0"><VideoPlayer mode="controls" src={form.media_url} poster={form.poster_url} className="w-full h-full" videoClassName="w-full h-full object-cover" testId="ad-preview-reels-video" /></div>
            : <img src={resolveMediaUrl(form.media_url)} onError={onMediaImgError} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>
            <span className="text-xs px-4 text-center">{t("ads.preview_visual_ph", "Votre visuel apparaîtra ici")}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}>
          {t("post.sponsored", "Sponsorisé")}
        </div>
        <div className="absolute inset-x-0 bottom-0 z-10 p-3 pt-12" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0))" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <img src={avatarSrc(user)} alt="" className="h-6 w-6 rounded-full object-cover border border-white/40" />
            <span className="text-white text-xs font-semibold truncate">{user?.name || t("ads.preview_page", "Votre page")}</span>
          </div>
          {form.body && <p className="text-white/90 text-[11px] leading-snug line-clamp-2 mb-2">{form.body}</p>}
          {(hasButton || form.cta_headline) && (
            <button type="button" className="w-full rounded-lg bg-white text-xs font-bold py-2 flex items-center justify-center gap-1" style={{ color: "#111" }}>
              {form.cta_headline || t(`ads.ctalabel_${form.cta_label}`, ctaLabel({ label: form.cta_label }))}
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdPreview;
